import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, ChevronRight, Play } from 'lucide-react';
import ContentReactBar from './ContentReactBar';

/**
 * PostCard — three variants used across Home carousels, Category grid pages,
 * and compact list rows. Every variant now surfaces the universal social
 * strip (ContentReactBar) so like/comment/share are one tap away from any
 * list view — no need to open the reflection first.
 */
const shareFor = (post) => ({
  kind: 'post',
  id: post.id,
  title: post.title,
  text: `"${post.title}" — a reflection on Reflections by Rowdy`,
  url: typeof window !== 'undefined' ? `${window.location.origin}/post/${post.id}` : '',
  recommendable: true,
});

const PostCard = ({ post, variant = 'default' }) => {
  const contentRef = `post:${post.id}`;

  if (variant === 'compact') {
    return (
      <div className="relative">
        <Link
          to={`/post/${post.id}`}
          className="flex items-center gap-4 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3 shadow-md hover:shadow-lg hover:border-[#D6AD60]/30 transition-all duration-300 group"
        >
          <img
            src={post.image}
            alt={post.title}
            className="w-20 h-20 rounded-xl object-cover flex-shrink-0 group-hover:scale-105 transition-transform duration-500"
          />
          <div className="min-w-0 flex-1">
            <h3
              className="text-sm font-semibold text-stone-800 mb-1 line-clamp-1 group-hover:text-[#D6AD60] transition-colors duration-300"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {post.title}
            </h3>
            <p className="text-xs text-stone-500 line-clamp-2 mb-2">
              {post.excerpt}
            </p>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-[10px] text-stone-400">
                <Calendar className="w-3 h-3" />
                {post.date}
              </span>
              <ContentReactBar contentRef={contentRef} share={shareFor(post)} size="sm" />
            </div>
          </div>
        </Link>
      </div>
    );
  }

  if (variant === 'grid') {
    return (
      <div className="relative">
        <Link
          to={`/post/${post.id}`}
          className="block bg-[#FFFCF3] border border-stone-200 rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:border-[#D6AD60]/30 transition-all duration-300 group"
        >
          <div className="relative overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            {(post.videoUrl || post.libraryRef) && (
              <div className="absolute top-2 right-2 w-7 h-7 bg-[#D6AD60] rounded-full flex items-center justify-center shadow-md">
                <Play className="w-3 h-3 text-[#1a1814] ml-0.5" fill="currentColor" />
              </div>
            )}
          </div>
          <div className="p-4">
            <h3
              className="text-sm font-semibold text-stone-800 mb-2 line-clamp-2 group-hover:text-[#D6AD60] transition-colors duration-300"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {post.title}
            </h3>
            <p className="text-xs text-stone-500 line-clamp-2 mb-3">
              {post.excerpt}
            </p>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-[10px] text-stone-400">
                <Calendar className="w-3 h-3" />
                {post.date}
              </span>
              <ContentReactBar contentRef={contentRef} share={shareFor(post)} size="sm" />
            </div>
          </div>
        </Link>
      </div>
    );
  }

  // Default card for carousel
  return (
    <div className="relative flex-shrink-0 w-[260px]">
      <Link
        to={`/post/${post.id}`}
        className="block bg-[#FFFCF3] border border-stone-200 rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:border-[#D6AD60]/30 transition-all duration-300 group"
      >
        <div className="relative overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          {(post.videoUrl || post.libraryRef) && (
            <div className="absolute top-2 right-2 w-7 h-7 bg-[#D6AD60] rounded-full flex items-center justify-center shadow-md">
              <Play className="w-3 h-3 text-[#1a1814] ml-0.5" fill="currentColor" />
            </div>
          )}
        </div>
        <div className="p-4">
          <h3
            className="text-sm font-semibold text-stone-800 mb-1.5 line-clamp-1 group-hover:text-[#D6AD60] transition-colors duration-300"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {post.title}
          </h3>
          <p className="text-xs text-stone-500 line-clamp-2 mb-3">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="flex items-center gap-1 text-[10px] text-stone-400 whitespace-nowrap">
                <Calendar className="w-3 h-3" />
                {post.date}
              </span>
              {post.comments > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-stone-400 whitespace-nowrap">
                  <MessageCircle className="w-3 h-3" />
                  {post.comments}
                </span>
              )}
            </div>
            <ContentReactBar contentRef={contentRef} share={shareFor(post)} size="sm" />
          </div>
          <div className="mt-2 flex justify-end">
            <span className="text-xs text-[#D6AD60] font-medium flex items-center gap-0.5 group-hover:text-[#AA8347] transition-colors duration-300">
              Read <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default PostCard;
